import { Box, Center, Divider, Flex, Text, VStack } from "@chakra-ui/react";
import React, { useCallback, useMemo } from "react";

import { LoadingIndicator } from "../../../../components";
import { formatDateRange, MS_PER_DAY } from "../../../../utils/datetime";
import {
  AiInterviewFeedbackRatingSummaryQuery,
  useAiInterviewFeedbackRatingSummaryQuery,
} from "../../../graphql";
import AnalyticsLineChart from "../../../pages/analytics/line-chart/AnalyticsLineChart";
import TooltipContainer from "../../../pages/analytics/line-chart/TooltipContainer";
import TooltipDataRow from "../../../pages/analytics/line-chart/TooltipDataRow";
import { xFormatter } from "../../../pages/analytics/line-chart/xFormatter";
import type { LineChartDataPoint } from "../../../pages/analytics/types";
import {
  computePreviousDateRange,
  getUnixFromISO,
} from "../../../pages/analytics/utils";
import {
  findComparisonPoint,
  formatChangeLabel,
  formatPctChange,
  shiftDateKeyByDays,
} from "./AiInterviewerFeedbackRatingCard";

const COLOR_RATINGS_COMMENTS = "#6B46C1";
const COLOR_RATINGS_ONLY = "#07719F";

type TimeSeriesRow = NonNullable<
  NonNullable<
    AiInterviewFeedbackRatingSummaryQuery["aiInterviewFeedbackRatingSummary"]
  >["timeSeries"]
>[number];

export type RatingsCommentsChartDatum = {
  comparisonBucketStart?: string;
  ratingOnlyCount: number;
  ratingWithCommentCount: number;
  reviewCount: number;
  totalInterviewCount: number;
  pctRatingsAndComments: number;
  pctRatingsOnly: number;
  xLabel: string;
};

function datumFromTimeSeriesPoint(
  point: TimeSeriesRow,
  comparisonBucketStart?: string
): RatingsCommentsChartDatum {
  const total = point.totalInterviewCount;
  const rwc = point.ratingWithCommentCount ?? 0;
  const rc = point.reviewCount;
  const ratingOnlyCount = Math.max(0, rc - rwc);
  const pctRatingsOnly = total > 0 ? (ratingOnlyCount / total) * 100 : 0;
  const pctRatingsAndComments = total > 0 ? (rwc / total) * 100 : 0;
  return {
    xLabel: point.date,
    comparisonBucketStart,
    totalInterviewCount: total,
    reviewCount: rc,
    ratingWithCommentCount: rwc,
    ratingOnlyCount,
    pctRatingsOnly,
    pctRatingsAndComments,
  };
}

type AiInterviewerRatingsCommentsCardProps = {
  aiInterviewerId: string;
  bucketInterval?: "DAY" | "WEEK";
  dateStart: Date;
  dateEnd: Date;
  loadingSummary?: boolean;
  summary?: NonNullable<
    AiInterviewFeedbackRatingSummaryQuery["aiInterviewFeedbackRatingSummary"]
  >;
};

const pctCountLabel = (pct: number, count: number): string =>
  `${Math.round(pct)}% (${count})`;

export const AiInterviewerRatingsCommentsCard: React.FC<
  AiInterviewerRatingsCommentsCardProps
> = ({
  aiInterviewerId,
  bucketInterval,
  dateStart,
  dateEnd,
  loadingSummary,
  summary,
}) => {
  const selectedRangeDays = useMemo(
    () =>
      Math.floor((dateEnd.getTime() - dateStart.getTime()) / MS_PER_DAY) + 1,
    [dateEnd, dateStart]
  );
  const { previousDateRangeStart, previousDateRangeEnd } = useMemo(
    () => computePreviousDateRange(dateStart, dateEnd),
    [dateEnd, dateStart]
  );

  const usesProvidedSummary =
    loadingSummary !== undefined || summary !== undefined;

  const { data, loading } = useAiInterviewFeedbackRatingSummaryQuery({
    variables: {
      aiInterviewerId,
      dateStart: dateStart.toISOString(),
      dateEnd: dateEnd.toISOString(),
      bucketInterval: bucketInterval ?? "DAY",
    },
    skip: usesProvidedSummary,
  });

  const { data: previousData } = useAiInterviewFeedbackRatingSummaryQuery({
    variables: {
      aiInterviewerId,
      dateStart: previousDateRangeStart.toISOString(),
      dateEnd: previousDateRangeEnd.toISOString(),
      bucketInterval: bucketInterval ?? "DAY",
    },
    skip:
      (usesProvidedSummary && (loadingSummary ?? false)) ||
      (usesProvidedSummary && (summary?.reviewCount ?? 0) === 0),
  });

  const currentSummary = usesProvidedSummary
    ? summary
    : data?.aiInterviewFeedbackRatingSummary;
  const currentSummaryLoading = usesProvidedSummary
    ? loadingSummary ?? false
    : loading;
  const previousSummary = previousData?.aiInterviewFeedbackRatingSummary;

  const chartData = useMemo(() => {
    if (!currentSummary?.timeSeries) return [];

    const previousPoints = previousSummary?.timeSeries ?? [];

    return currentSummary.timeSeries
      .filter((point) => point.totalInterviewCount > 0)
      .map((point) => {
        const comparisonPoint = findComparisonPoint({
          bucketInterval: bucketInterval ?? "DAY",
          currentPointDate: point.date,
          previousPoints,
          selectedRangeDays,
        });

        const datum = datumFromTimeSeriesPoint(
          point,
          comparisonPoint?.date ??
            shiftDateKeyByDays(point.date, -selectedRangeDays)
        );
        return {
          x: getUnixFromISO(point.date),
          pctRatingsAndComments: datum.pctRatingsAndComments,
          pctRatingsOnly: datum.pctRatingsOnly,
          datum,
          compareDatum: comparisonPoint
            ? datumFromTimeSeriesPoint(comparisonPoint)
            : undefined,
        };
      });
  }, [
    bucketInterval,
    currentSummary?.timeSeries,
    previousSummary?.timeSeries,
    selectedRangeDays,
  ]);

  const dateRangeLabel = formatDateRange(dateStart, dateEnd);

  const formatBucketTitle = useCallback(
    (isoDate: string): string => {
      const formatted = xFormatter(
        getUnixFromISO(isoDate),
        bucketInterval ?? "DAY",
        1
      );
      return (bucketInterval ?? "DAY") === "WEEK"
        ? `Week of ${formatted}`
        : formatted;
    },
    [bucketInterval]
  );

  const customTooltipContent = useCallback(
    (props: Record<string, unknown>) => {
      const active = props.active as boolean | undefined;
      const payload = props.payload as
        | ReadonlyArray<{
            payload?: {
              compareDatum?: RatingsCommentsChartDatum;
              datum: RatingsCommentsChartDatum;
            };
          }>
        | undefined;
      if (!active || !payload?.length) return null;
      const row = payload[0]?.payload;
      if (!row?.datum) return null;
      const d = row.datum;
      const c = row.compareDatum;

      const changeVsLabel = c?.xLabel
        ? `Change vs ${formatChangeLabel(formatBucketTitle(c.xLabel))}`
        : d.comparisonBucketStart
        ? `Change vs ${formatChangeLabel(
            formatBucketTitle(d.comparisonBucketStart)
          )}`
        : "Change vs previous period";

      return (
        <TooltipContainer width="280px">
          <VStack w="100%" align="stretch" spacing={1}>
            <Text fontWeight="semibold" w="100%">
              {formatBucketTitle(d.xLabel)}
            </Text>
            <TooltipDataRow
              bulletColor={COLOR_RATINGS_ONLY}
              label="Ratings only"
              value={pctCountLabel(d.pctRatingsOnly, d.ratingOnlyCount)}
            />
            <TooltipDataRow
              bulletColor={COLOR_RATINGS_COMMENTS}
              label="Ratings & comments"
              value={pctCountLabel(
                d.pctRatingsAndComments,
                d.ratingWithCommentCount
              )}
            />
            <TooltipDataRow
              label="Total interviews"
              value={`${d.totalInterviewCount}`}
            />
            <Divider borderColor="whiteAlpha.400" />
            <Text fontSize="xs" color="whiteAlpha.800" w="100%">
              {changeVsLabel}
            </Text>
            <TooltipDataRow
              label="Ratings only %"
              value={formatPctChange(d.pctRatingsOnly, c?.pctRatingsOnly)}
            />
            <TooltipDataRow
              label="Ratings & comments %"
              value={formatPctChange(
                d.pctRatingsAndComments,
                c?.pctRatingsAndComments
              )}
            />
          </VStack>
        </TooltipContainer>
      );
    },
    [formatBucketTitle]
  );

  if (currentSummaryLoading && !currentSummary) {
    return (
      <Box
        border="1px solid"
        borderColor="gray.300"
        borderRadius="12px"
        p={6}
        bg="white"
      >
        <Box
          display="grid"
          gridTemplateColumns="260px 80px minmax(0, 1fr)"
          gridTemplateRows="auto auto"
          rowGap={4}
          alignItems="start"
        >
          <Box gridColumn="1" gridRow="1">
            <Text fontWeight="semibold" whiteSpace="nowrap">
              Ratings & Comments
            </Text>
          </Box>
          <Box gridColumn="3" gridRow="1">
            <Text fontSize="sm" color="gray.600" textAlign="right">
              {dateRangeLabel}
            </Text>
          </Box>
          <Center gridColumn="1 / 4" gridRow="2" py="80px">
            <LoadingIndicator delay={0} />
          </Center>
        </Box>
      </Box>
    );
  }

  if (!currentSummary) return null;

  const { totalInterviewCount } = currentSummary;
  const { reviewCount } = currentSummary;
  const pctWithRatings =
    totalInterviewCount > 0
      ? Math.round((reviewCount / totalInterviewCount) * 100)
      : null;

  return (
    <Box
      border="1px solid"
      borderColor="gray.300"
      borderRadius="12px"
      p={6}
      bg="white"
    >
      <Box
        display="grid"
        gridTemplateColumns="260px 80px minmax(0, 1fr)"
        gridTemplateRows="auto auto auto"
        rowGap={4}
        alignItems="start"
      >
        <Box gridColumn="1" gridRow="1">
          <Text whiteSpace="nowrap">
            <Text as="span" fontWeight="semibold">
              Ratings & Comments
            </Text>{" "}
            <Text as="span" fontSize="12px" color="gray.500">
              (
              <Text as="span" fontStyle="italic">
                n
              </Text>
              ={totalInterviewCount})
            </Text>
          </Text>
        </Box>

        <Box gridColumn="3" gridRow="1">
          <Text fontSize="sm" color="gray.600" textAlign="right">
            {dateRangeLabel}
          </Text>
        </Box>

        <Flex gridColumn="1" gridRow="2" alignItems="baseline" gap={1}>
          <Text
            fontSize="42px"
            fontWeight={500}
            color="#393F47"
            lineHeight="56px"
          >
            {pctWithRatings != null ? `${pctWithRatings}%` : "--"}
          </Text>
        </Flex>

        {chartData.length > 0 && (
          <>
            {/* Same footprint as Feedback card row-3 distribution so chart grid height matches. */}
            <Flex
              gridColumn="1"
              gridRow="3"
              direction="column"
              gap="2px"
              mt="-10px"
              width="260px"
              pr="44px"
              boxSizing="border-box"
              visibility="hidden"
              aria-hidden
              pointerEvents="none"
            >
              {[5, 4, 3, 2, 1].map((rating) => (
                <Flex key={rating} gap="6px" alignItems="center" width="100%">
                  <Text
                    fontSize="12px"
                    fontWeight={500}
                    color="#67717E"
                    width="8px"
                    textAlign="right"
                  >
                    {rating}
                  </Text>
                  <Box
                    flex={1}
                    bg="#E5E7EA"
                    borderRadius="999px"
                    overflow="hidden"
                  >
                    <Box height="6px" borderRadius="999px" width="0%" />
                  </Box>
                  <Text
                    fontSize="12px"
                    fontWeight={500}
                    color="#67717E"
                    width="24px"
                    textAlign="right"
                    whiteSpace="nowrap"
                  >
                    0%
                  </Text>
                </Flex>
              ))}
            </Flex>
            <Box
              gridColumn="3"
              gridRow="2 / 4"
              minW={0}
              h="100%"
              pt="24px"
              overflow="visible"
              sx={{
                "& .recharts-wrapper, & .recharts-surface": {
                  overflow: "visible !important",
                },
              }}
            >
              <AnalyticsLineChart
                chartVariant="area"
                reverseLegend={false}
                areaSeries={[
                  {
                    dataKey: "pctRatingsAndComments",
                    name: "Ratings & Comments",
                    fill: COLOR_RATINGS_COMMENTS,
                    stroke: COLOR_RATINGS_COMMENTS,
                  },
                  {
                    dataKey: "pctRatingsOnly",
                    name: "Ratings Only",
                    fill: COLOR_RATINGS_ONLY,
                    stroke: COLOR_RATINGS_ONLY,
                  },
                ]}
                areaStackId="ratingsComments"
                customTooltipContent={customTooltipContent}
                data={chartData as unknown as LineChartDataPoint[]}
                dataKey="pctRatingsAndComments"
                drawDot={false}
                yAxis={{
                  domain: [0, 100],
                  interval: 0,
                  ticks: [0, 50, 100],
                  formatter: (v) => `${v}%`,
                }}
                bucketInterval={bucketInterval ?? "DAY"}
                bucketSize={1}
                rangeDisplayValues={{
                  current: dateRangeLabel,
                  previous: "",
                }}
                chartMargin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                chartHeight="100%"
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
